// ============================================================================
// Training defaults & UI prefs — shared between Settings and the run Configuration
// ----------------------------------------------------------------------------
// The Configs hyperparameter panel ships with a fixed set of starting values
// (Global Rounds, Local Epochs, Batch Size, Learning Rate, Model Architecture).
// The Settings page lets a user change those starting values.
//
// Persistence model: the server is the source of truth (per-user, stored in the
// `users.preferences` JSONB column behind GET/PUT /api/user/preferences) and
// localStorage is a write-through cache so first paint is instant and the app
// keeps working offline.
//   • loadTrainingDefaults()  — synchronous cache read (used by the Configs
//     useState initializer for instant pre-fill).
//   • fetchTrainingDefaults() — async; pulls the authoritative values from the
//     server, refreshes the cache, returns them. Call on login / Settings mount.
//   • saveTrainingDefaults()  — async; PUTs to the server when online and always
//     updates the cache. Resolves to { synced: boolean }.
//
// Keys intentionally match the field names used in scenes/configs/index.jsx so
// the values drop straight into that screen's `configs` state.
// ============================================================================
import { apiGet, apiPut } from "../utils/api";

const STORAGE_KEY = "fx_training_defaults";
const UI_STORAGE_KEY = "fx_ui_prefs";
// Legacy standalone key — migrated into UI prefs on first load, then dropped.
const LEGACY_TZ_KEY = "fx_timezone";

const PREFERENCES_ENDPOINT = "/user/preferences";

// Canonical platform defaults (identical to the values the Configs screen
// historically hard-coded). Used as the fallback and the "reset" target.
export const DEFAULT_TRAINING_DEFAULTS = {
  model_type: "logreg",
  "num-server-rounds": 3,
  "local-epochs": 1,
  "batch-size": 32,
  "learning-rate": 0.01,
};

export const DEFAULT_UI_PREFS = {
  language: "en",
  timezone: "America/Toronto",
};

// ── local cache helpers ────────────────────────────────────────────────────

// Read the saved defaults, merged over the canonical ones so a missing or
// partial record never leaves a field undefined.
export const loadTrainingDefaults = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_TRAINING_DEFAULTS };
    return { ...DEFAULT_TRAINING_DEFAULTS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_TRAINING_DEFAULTS };
  }
};

const writeTrainingCache = (values) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(values));
  } catch {
    /* storage unavailable — fall back to in-memory defaults */
  }
};

export const loadUiPrefs = () => {
  try {
    const raw = localStorage.getItem(UI_STORAGE_KEY);
    const base = raw ? { ...DEFAULT_UI_PREFS, ...JSON.parse(raw) } : { ...DEFAULT_UI_PREFS };
    // One-time migration of the legacy standalone timezone key.
    if (!raw) {
      const legacyTz = localStorage.getItem(LEGACY_TZ_KEY);
      if (legacyTz) base.timezone = legacyTz;
    }
    return base;
  } catch {
    return { ...DEFAULT_UI_PREFS };
  }
};

const writeUiCache = (values) => {
  try {
    localStorage.setItem(UI_STORAGE_KEY, JSON.stringify(values));
    // Drop the legacy key once migrated.
    localStorage.removeItem(LEGACY_TZ_KEY);
  } catch {
    /* storage unavailable */
  }
};

// ── server-backed reads / writes ────────────────────────────────────────────

// Pull the authoritative preferences from the server, refresh the cache, and
// return both namespaces. Falls back to the local cache when offline.
export const fetchTrainingDefaults = async () => {
  try {
    const data = await apiGet(PREFERENCES_ENDPOINT, { suppressAuthError: true });
    const prefs = data?.preferences || {};

    const training = { ...DEFAULT_TRAINING_DEFAULTS, ...(prefs.training_defaults || {}) };
    const ui = { ...DEFAULT_UI_PREFS, ...(prefs.ui || {}) };

    writeTrainingCache(training);
    writeUiCache(ui);

    return { training, ui, synced: true };
  } catch (error) {
    console.warn(
      "[trainingDefaults] fetch failed, using local cache:",
      error?.message || error
    );
    return { training: loadTrainingDefaults(), ui: loadUiPrefs(), synced: false };
  }
};

// Persist the training defaults. Writes to the server when online; always
// updates the local cache so the value survives a reload even if the network
// call failed. Resolves to { synced } so the UI can tailor its toast.
export const saveTrainingDefaults = async (values) => {
  writeTrainingCache(values);
  try {
    await apiPut(PREFERENCES_ENDPOINT, { preferences: { training_defaults: values } });
    return { synced: true };
  } catch (error) {
    console.warn(
      "[trainingDefaults] server save failed, cached locally:",
      error?.message || error
    );
    return { synced: false };
  }
};

// Persist UI prefs (language / timezone) the same way.
export const saveUiPrefs = async (values) => {
  writeUiCache(values);
  try {
    await apiPut(PREFERENCES_ENDPOINT, { preferences: { ui: values } });
    return { synced: true };
  } catch (error) {
    console.warn(
      "[trainingDefaults] server UI prefs save failed, cached locally:",
      error?.message || error
    );
    return { synced: false };
  }
};
